import React, { useRef, useState } from 'react';
import { Button, Modal, Row, Col } from 'antd';
import { map } from 'lodash';

import { getFullName } from 'utils/personUtils';
import { getLocation } from 'utils/locationUtils';
import BasePageCard from 'components/wrappers/BasePageCard';
import { getWebsiteLink, getWebsiteName } from 'utils/urlUtils';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import { BOUserTypeEnum, FullBOUserResponseModel } from 'api/adminApiClient';
import { getAccountTypeLabel, getProfileTypeLabel } from 'utils/accountUtils';
import CompanyProfileModal from 'components/modals/ProfileModals/CompanyProfileModal';
import SelfEmployedProfileModal from 'components/modals/ProfileModals/SelfEmployedProfileModal';
import InternationalProfileModal from 'components/modals/ProfileModals/InternationalProfileModal';
import { useAppSelector } from 'hooks/useAppSelector';

type Props = {
  recruiterDetails: FullBOUserResponseModel;
  loadRecruiterDetails: () => Promise<void>;
};

export default function GeneralInfoSection(props: Props) {
  const { recruiterDetails, loadRecruiterDetails } = props;
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const isAdmin = useAppSelector(isAdminSelector);
  const formRef: any = useRef(null);
  const {
    memberKey,
    boUserType,
    profileType,
    ownerInfo,
    bestContact,
    businessInfo,
    registeredEmail,
    website,
    mbpContactsInfo,
    activityReportRecipients,
  } = recruiterDetails;
  const mbp =
    mbpContactsInfo ?? (recruiterDetails as { MBPContactsInfo?: typeof mbpContactsInfo })?.MBPContactsInfo;
  const ownerFullName = getFullName(ownerInfo?.firstName, ownerInfo?.lastName);
  const bestContactFullName = getFullName(
    bestContact?.firstName,
    bestContact?.lastName,
  );
  const mailingAddress = getLocation(
    businessInfo?.location?.countryName,
    businessInfo?.location?.address,
    businessInfo?.location?.state ||
      businessInfo?.location?.internationalStateName,
    businessInfo?.location?.city ||
      businessInfo?.location?.internationalCityName,
    businessInfo?.location?.zip,
  );

  const handleModalCancel = () => {
    if (formRef?.current.dirty) {
      return Modal.confirm({
        title: 'Are you sure? The data will not be saved.',
        icon: <span className="icon icon-attention-filled" />,
        onOk() {
          handleCloseEditModal();
        },
        onCancel() {},
      });
    }

    handleCloseEditModal();
  };

  const handleEditSuccess = async () => {
    await loadRecruiterDetails();
    handleCloseEditModal();
  };

  const handleOpenEditModal = () => {
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
  };

  const getGeneralInfoModalByUserType = () => {
    const editModalSettings = {
      title: 'Edit general information',
      maxWidth: 888,
      visible: isEditModalOpen,
      onCancel: handleModalCancel,
    };

    const editModalProps = {
      formRef,
      isAdmin,
      modalProps: editModalSettings,
      onSuccess: handleEditSuccess,
      profileInfo: recruiterDetails,
      profileType: recruiterDetails.profileType,
    };

    switch (boUserType) {
      case BOUserTypeEnum.Company:
        return <CompanyProfileModal {...editModalProps} />;
      case BOUserTypeEnum.SelfEmployed:
        return <SelfEmployedProfileModal {...editModalProps} />;
      case BOUserTypeEnum.International:
        return <InternationalProfileModal {...editModalProps} />;

      default:
        return null;
    }
  };

  return (
    <BasePageCard
      title="General Information"
      className="stretch-card"
      icons={<span className="icon icon-writing-tool-user"></span>}
      subTitle={
        <Button
          onClick={handleOpenEditModal}
          className="with-icon mobile-icon-only"
          size="large"
          icon={<span className="icon icon-edit" />}
        >
          Edit
        </Button>
      }
    >
      <div className="content-wrapper">
        {isEditModalOpen && getGeneralInfoModalByUserType()}

        <Row gutter={[4, 4]} className="titled-sheet">
          <Col xs={24} md={12}>
            <div className="title">Account type:</div>
            <div>{getAccountTypeLabel(boUserType)}</div>
          </Col>
          <Col xs={24} md={12}>
            <div className="title">Profile type:</div>
            <div>{getProfileTypeLabel(profileType)}</div>
          </Col>
          <Col xs={24} md={12}>
            <div className="title">Member ID:</div>
            <div>{memberKey}</div>
          </Col>
          {!!registeredEmail && (
            <Col xs={24} md={12}>
              <div className="title">Member email:</div>
              <div>{registeredEmail}</div>
            </Col>
          )}
          {!!ownerFullName && (
            <Col xs={24} md={12}>
              <div className="title">Company representative name:</div>
              <div>{ownerFullName}</div>
            </Col>
          )}
          {!!ownerInfo?.email && (
            <Col xs={24} md={12}>
              <div className="title">Company representative email:</div>
              <div>{ownerInfo?.email}</div>
            </Col>
          )}
          {!!bestContactFullName && (
            <Col xs={24} md={12}>
              <div className="title">Best point of contact:</div>
              <div>{bestContactFullName}</div>
            </Col>
          )}
          {!!bestContact?.email && (
            <Col xs={24} md={12}>
              <div className="title">Email for communications:</div>
              <div>{bestContact?.email}</div>
            </Col>
          )}
          {!!businessInfo?.name && (
            <Col xs={24} md={12}>
              <div className="title">Business name:</div>
              <div>{businessInfo?.name}</div>
            </Col>
          )}
          {!!businessInfo?.businessEmail && (
            <Col xs={24} md={12}>
              <div className="title">Business email:</div>
              <div>{businessInfo?.businessEmail}</div>
            </Col>
          )}
          {!!businessInfo?.phoneNumber && (
            <Col xs={24} md={12}>
              <div className="title">Business phone:</div>
              <div>{businessInfo?.phoneNumber}</div>
            </Col>
          )}
          {!!mailingAddress && (
            <Col xs={24} md={12}>
              <div className="title">Mailing address:</div>
              <div>{mailingAddress}</div>
            </Col>
          )}
          {!!businessInfo?.industries?.length && (
            <Col xs={24} md={12}>
              <div className="title">Industries:</div>
              <div>
                {map(
                  businessInfo?.industries,
                  (industry) => industry.name,
                ).join(', ')}
              </div>
            </Col>
          )}
          {!!businessInfo?.assignmentLocations?.length && (
            <Col xs={24} md={12}>
              <div className="title">States for placements:</div>
              <div>
                {map(
                  businessInfo?.assignmentLocations,
                  (location) => location.name,
                ).join(', ')}
              </div>
            </Col>
          )}
          {!!website && (
            <Col xs={24} md={12}>
              <div className="title">Website:</div>
              <div>
                <a
                  href={getWebsiteLink(website)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {getWebsiteName(website)}
                </a>
              </div>
            </Col>
          )}

          {(() => {
            const mbpPascal = mbp as {
              ClientSuccessPartner?: string;
              BDSalesContact?: string;
              BillingSpecialist?: string;
            };
            const clientSuccessPartnerDisplay =
              (mbp?.clientSuccessPartner ?? mbpPascal?.ClientSuccessPartner)?.trim() ?? '';
            const bdSalesContactDisplay =
              (mbp?.bdSalesContact ?? mbpPascal?.BDSalesContact)?.trim() ?? '';
            const billingSpecialistDisplay =
              (mbp?.billingSpecialist ?? mbpPascal?.BillingSpecialist)?.trim() ?? '';
            const hasClientSuccessPartner = clientSuccessPartnerDisplay.length > 0;
            const hasBdSalesContact = bdSalesContactDisplay.length > 0;
            const hasBillingSpecialist = billingSpecialistDisplay.length > 0;
            const hasAnyMbp =
              hasClientSuccessPartner || hasBdSalesContact || hasBillingSpecialist;
            if (!hasAnyMbp) return null;
            return (
              <>
                {hasClientSuccessPartner && (
                  <Col xs={24} md={12}>
                    <div className="title">Client Success Partner:</div>
                    <div>{clientSuccessPartnerDisplay}</div>
                  </Col>
                )}
                {hasBdSalesContact && (
                  <Col xs={24} md={12}>
                    <div className="title">BD Sales Contact:</div>
                    <div>{bdSalesContactDisplay}</div>
                  </Col>
                )}
                {hasBillingSpecialist && (
                  <Col xs={24} md={12}>
                    <div className="title">Billing Specialist:</div>
                    <div>{billingSpecialistDisplay}</div>
                  </Col>
                )}
              </>
            );
          })()}
          {!!activityReportRecipients?.recipientEmail1 && (
            <Col xs={24} md={24}>
              <div className="title">Activity Report Recipient 1:</div>
              <div>{activityReportRecipients.recipientEmail1}</div>
            </Col>
          )}
          {!!activityReportRecipients?.recipientEmail2 && (
            <Col xs={24} md={24}>
              <div className="title">Activity Report Recipient 2:</div>
              <div>{activityReportRecipients.recipientEmail2}</div>
            </Col>
          )}
          {!!activityReportRecipients?.recipientEmail3 && (
            <Col xs={24} md={24}>
              <div className="title">Activity Report Recipient 3:</div>
              <div>{activityReportRecipients.recipientEmail3}</div>
            </Col>
          )}
        </Row>
      </div>
    </BasePageCard>
  );
}
